import { Agent } from 'models/agent.model';
import { Page } from 'models/page';
import api from '../api/instance';
import { SystemInfo } from '@modules/agent/models/system-info.model';
import { Pageable } from '@models/pageable';
import { CurrentExecutingStepModel } from '@modules/agent/models/current-executing-step.model';
import { TestStepResultPageModel } from '@testsigmainc/models';
import { getAgentUrl } from '@utils/agent-url.utils';

const nameSpace = '/settings/agents';
const agentUrl = '/agent/api/v1/agent';

const findAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
) => {
  query = query + ',agentType!TRANSIENT';
  return api
    .get(nameSpace, { query, sort, size, page })
    .then((res) => new Page<Agent>().deserialize(res, Agent))
    .catch((e) => Promise.reject(e));
};

const show = (id: number) => {
  return api
    .get(nameSpace + '/' + id)
    .then((res) => new Agent().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const destroy = (id: number): Promise<void> => {
  return api
    .remove(nameSpace + '/' + id)
    .then((res) => console.log(res))
    .catch((err) => new Promise((_, reject) => reject(err)));
};

const find = (id: number): Promise<Agent> => {
  return api
    .get(nameSpace + '/' + id)
    .then((res) => new Agent().deserialize(res))
    .catch((err) => new Promise((_, reject) => reject(err)));
};

const update = (id: number, payload: Agent): Promise<Agent> => {
  return api
    .put(nameSpace + '/' + id, payload.serialize())
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

// ping agent
const ping = async (): Promise<SystemInfo> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(localAgentUrl + '/agent/api/v1/agent/system_info')
    .then((res) => new SystemInfo().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const stopDebuggerDriver = async (tenantId, testCaseResultId): Promise<any> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/stop_web_debugger/${tenantId}/${testCaseResultId}`,
    )
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const checkAppConnection = async (notify: boolean = false): Promise<any> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      localAgentUrl +
        `/agent/api/v1/agent/check_app_connection?notify=${notify}`,
    )
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

// agent is visible
const findByUuid = (agentId: string): Promise<Agent> => {
  return api
    .get(nameSpace + '/' + agentId + '/uuid')
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

const amdsAbsent = async (uuid?: string) => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get<boolean>(localAgentUrl + agentUrl + (uuid ? '/' + uuid : '') + '/amds')
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

// creating agent
const create = (agent: Agent): Promise<Agent> => {
  return api
    .post(nameSpace, agent)
    .then((res) => new Agent().deserialize(res))
    .catch((err) => Promise.reject(err));
};

// registering agent
const registerAgent = async (agent: Agent): Promise<void> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .put(
      localAgentUrl +
        '/agent/api/v1/' +
        agent.uniqueId +
        '/register?jwtApiKey=' +
        agent.jwtApiKey,
      null,
    )
    .then((res) => console.log(res))
    .catch((err) => Promise.reject(err));
};

const findAllPrivateAndPublic = (
  filter?: string,
  sortBy?: string,
  pageable?: Pageable,
): Promise<Page<Agent>> => {
  filter = filter + ',agentType!TRANSIENT';
  return api
    .get(nameSpace + 'all/', { filter })
    .then((res) => new Page<Agent>().deserialize(res, Agent))
    .catch((err) => Promise.reject(err));
};

const getCurrentExecutingStep = async (
  sessionId: string,
  tenantId: number,
  testCaseResultId: number,
): Promise<CurrentExecutingStepModel> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/get_current_executing_step/${tenantId}/${sessionId}/${testCaseResultId}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const resumeExecution = async (
  sessionId: string,
  tenantId: number,
  testCaseResultId: number,
  stepResultId: string,
  isReRun: boolean,
): Promise<CurrentExecutingStepModel> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/resume_execution/${stepResultId}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const skipOverExecution = async (
  tenantId: number,
  testCaseResultId: number,
  stepResultId: string,
): Promise<CurrentExecutingStepModel> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/skip_over/${stepResultId}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const stopExecution = async (
  tenantId: number,
  testCaseResultId: number,
): Promise<any> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/stop_execution/${tenantId}/${testCaseResultId}`,
    )
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const findAllDebugStepResults = async (
  tenantId: number,
  testCaseResultId: number,
) => {
  const localAgentUrl = await getAgentUrl();
  return new Promise((resolve, reject) => {
    api
      .get(
        `${localAgentUrl}${agentUrl}/step_results/${tenantId}/${testCaseResultId}`,
      )
      .then((res) => resolve(new TestStepResultPageModel().deserialize(res)))
      .catch((error) => reject(error));
  });
};

const reportAgent = (formData): Promise<any> =>
  api.post(`${nameSpace}/reportAgentLogs`, formData, 'multipart/form-data');

const getAgentDownloadUrl = (): Promise<any> => {
  return api.get(nameSpace + '/download_url');
};

const stepOverExecution = async (
  tenantId: number,
  testCaseResultId: number,
  stepResultUuid: string,
): Promise<CurrentExecutingStepModel> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(
      `${localAgentUrl}/agent/api/v1/device_sessions/step_over/${stepResultUuid}`,
    )
    .then((data) => new CurrentExecutingStepModel().deserialize(data))
    .catch((err) => Promise.reject(err));
};

const restartExecution = async (
  tenantId: number,
  testCaseResultId: number,
): Promise<CurrentExecutingStepModel> => {
  const localAgentUrl = await getAgentUrl();
  return api.get(
    `${localAgentUrl}/agent/api/v1/device_sessions/restart_execution`,
  );
};

const updateDebugPoints = async (
  testCaseResultId: number,
  debugPoints: number[],
): Promise<any> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .put(`${localAgentUrl}/agent/api/v1/device_sessions/debugpoints`, {
      testCaseResultId,
      debugPoints,
    })
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

const getLiveTestCaseResult = async (): Promise<any> => {
  const localAgentUrl = await getAgentUrl();
  return api
    .get(`${localAgentUrl}/agent/api/v1/device_sessions/test_case_result`)
    .then((data) => data)
    .catch((err) => Promise.reject(err));
};

export const AgentService = {
  findAll,
  show,
  destroy,
  find,
  update,
  ping,
  checkAppConnection,
  findByUuid,
  amdsAbsent,
  create,
  registerAgent,
  findAllPrivateAndPublic,
  getCurrentExecutingStep,
  resumeExecution,
  skipOverExecution,
  findAllDebugStepResults,
  stopExecution,
  reportAgent,
  stopDebuggerDriver,
  getAgentDownloadUrl,
  stepOverExecution,
  restartExecution,
  updateDebugPoints,
  getLiveTestCaseResult,
};
